<script setup lang="ts">
defineOptions({
  name: 'MopCheeringMessage',
});

const props = defineProps({
  message: {
    type: String,
    required: true,
  },
  backgroundColor: {
    type: String,
    default: null,
    validator(value: string) {
      return ['purple'].includes(value);
    },
  },
  showIcon: {
    type: Boolean,
    default: false,
  },
  showArrow: {
    type: Boolean,
    default: false,
  },
  isNotClosable: {
    type: Boolean,
    default: false,
  },
  fadeOutInSeconds: {
    type: Number,
    default: 0,
  },
  textCenter: {
    type: Boolean,
    default: false,
  },
});

const { $breakpoint } = useNuxtApp();

const isHiddenRef = ref(false);
const isVisibleRef = ref(true);

function handleCheeringMessageClick() {
  if (props.isNotClosable) {
    return;
  }
  hideMessage();
}

if (props.fadeOutInSeconds) {
  setTimeout(() => {
    hideMessage();
  }, props.fadeOutInSeconds * 1000);
}

function hideMessage() {
  isHiddenRef.value = true;
  setTimeout(() => {
    isVisibleRef.value = false;
  }, 300);
}

const showCheeringMessageRef = computed(() => {
  return props.message && isVisibleRef.value && $breakpoint.isMobileRef;
});
</script>

<template>
  <div
    v-if="showCheeringMessageRef"
    :class="[
      'cheering-message',
      {
        'cheering-message--arrow': showArrow,
        'cheering-message--hidden': isHiddenRef,
        'cheering-message--icon': showIcon,
        'cheering-message--text-center': textCenter,
        'cheering-message--purple': backgroundColor === 'purple',
      },
    ]"
    @click="handleCheeringMessageClick"
  >
    {{ message }}
  </div>
</template>

<style scoped lang="scss">
$cheering-icon-size: $space30;
$cheering-vertical-padding: 6px;
$arrow-size: $space10;
/* stylelint-disable-next-line color-no-hex */
$color-purple: #8a8eff;

.cheering-message {
  @include z(global, content);
  @include text-style(common);

  color: $white;
  animation: fadeIn 0.5s;
  transition: opacity ease 0.3s;
  padding: $cheering-vertical-padding $global-padding;
  line-height: 14px;
  background-color: $sustainability;
  position: relative;

  @include apply-from(medium) {
    display: none;
  }
}

.cheering-message--text-center {
  text-align: center;
}

.cheering-message--icon {
  padding-left: $cheering-icon-size + ($cheering-vertical-padding * 2);
  min-height: $cheering-icon-size + ($cheering-vertical-padding * 2);
  display: flex;
  align-items: center;

  @include apply-from(medium) {
    display: none;
  }

  &::before {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: $cheering-vertical-padding;
    width: $cheering-icon-size;
    height: $cheering-icon-size;
    content: '';
    background: url('data:image/svg+xml;base64,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')
      center center / contain no-repeat;
  }
}

.cheering-message--hidden {
  opacity: 0;
}

.cheering-message--arrow {
  &::after {
    content: '';
    position: absolute;
    right: calc(50% - #{$arrow-size});
    top: -9px;
    border-top: none;
    border-right: $arrow-size solid transparent;
    border-left: $arrow-size solid transparent;
    border-bottom: ($arrow-size * 1.5) solid $sustainability;
  }
}

.cheering-message--purple {
  background-color: $color-purple;
}

.cheering-message--purple.cheering-message--arrow {
  &::after {
    border-bottom-color: $color-purple;
  }
}
</style>
